<template>
    <div class="reports profiles-with-navigation no-print">
        <ReportSideBar
            :id="Number(reportData.id)"
            :reports-list="defaultReports"
            :custom-reports-list="customReports"
            :collapsed="true"
            :report-mode="custom === 'custom' ? 'edit' : 'add'"
        />
        <div class="component-with-nav-body">
            <div class="report-filters">
                <h4>{{ reportData.name }}</h4>
                <div class="report-form-filters">
                    <Hide v-model="filtersVisibility">
                        <h4>{{ $t('reports.filters') }}</h4>
                    </Hide>
                </div>
                <table v-show="filtersVisibility" class="report-filter-table">
                    <tr v-if="custom === 'custom'">
                        <td>{{ $t('reports.labels.type') }}:</td>
                        <td>{{ reportData.typeName }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('reporting.commonLabels.aAreas') }}:</td>
                        <td>
                            {{
                                reportData.type === REPORT_TYPES.ASCOM_EVENT
                                    ? aaForEvent
                                    : selectedAANames.join(', ')
                            }}
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('reporting.commonLabels.locations') }}:</td>
                        <td>
                            {{
                                reportData.type === REPORT_TYPES.ASCOM_EVENT
                                    ? reportData.event_location
                                    : selectedLocationNames.join(', ')
                            }}
                        </td>
                    </tr>
                    <tr v-if="reportData.type !== REPORT_TYPES.EXCEPTION">
                        <td>{{ $t('reporting.commonLabels.callTypes') }}:</td>
                        <td>
                            {{
                                reportData.type === REPORT_TYPES.ASCOM_EVENT
                                    ? reportData.event_types
                                    : selectedCallTypeNames.join(', ')
                            }}
                        </td>
                    </tr>
                    <tr v-if="reportData.type === REPORT_TYPES.EXCEPTION">
                        <td>{{ $t('reporting.commonLabels.callType') }}:</td>
                        <td>{{ callTypeSettings }}</td>
                    </tr>
                    <tr v-if="reportData.type === REPORT_TYPES.STAFF_PERFORMANCE">
                        <td>{{ $t('reporting.commonLabels.callId') }}</td>
                        <td>{{ reportData.call_id + ' (' + reportData.call_id_names + ')' }}</td>
                    </tr>
                    <tr v-if="reportData.type !== REPORT_TYPES.ASCOM_EVENT">
                        <td>{{ $t('reporting.commonLabels.period') }}:</td>
                        <td>{{ startEndTime }}</td>
                    </tr>
                    <tr v-if="reportData.type === REPORT_TYPES.PAIR_EVENT">
                        <td>{{ $t('reports.labels.eventPair') }}:</td>
                        <td>{{ selectedEventPair }}</td>
                    </tr>
                    <tr v-if="reportData.type === REPORT_TYPES.ASCOM_EVENT">
                        <td>{{ $t('reporting.commonLabels.startTimeDate') }}:</td>
                        <td>{{ formatDateString(reportData.start) }}</td>
                    </tr>
                    <tr v-if="reportData.type === REPORT_TYPES.ASCOM_EVENT">
                        <td>{{ $t('reporting.commonLabels.endTimeDate') }}:</td>
                        <td>{{ ascomEventEndTime }}</td>
                    </tr>
                </table>

                <button v-show="filtersVisibility" class="btn btn-undo" @click="back()">
                    <i class="icon-arrow-left"></i>{{ $t('reports.back') }}
                </button>
            </div>
            <div
                :is="reportingComponent"
                :report-parameters="reportParameters"
                @ascomEventEndTime="setAscomEventEndTime"
            />
        </div>
        <SaveReportModal
            v-if="custom !== 'custom'"
            :existed-reports="customReports"
            :shown="showSaveReportModal"
            :default-report-name="defReportName"
            @close="showSaveReportModal = false"
            @save="saveDefaultReport"
        />
        <MariTimeClock v-show="false" />
        <!-- For downloading a file in FireFox needs real link in DOM of document-->
        <a v-show="false" id="download-link" ref="download" href="#" target="_blank">download</a>
    </div>
</template>

<script type="text/babel">
import ReportSideBar from './ReportSideBar.vue';
import LocationReport from './report-types/LocationReport.vue';
import FullEvent from './report-types/FullEvent.vue';
import AscomEvent from './report-types/AscomEvent.vue';
import PairEvent from './report-types/PairEvent.vue';
import ExceptionReport from './report-types/ExceptionReport.vue';
import StaffPerformance from './report-types/StaffPerformance.vue';
import SaveReportModal from './SaveReportModal.vue';
import mappingState from '../common/mappingState';
import reportingUtils, {
    saveReport,
    getCustomReportObject,
    getSelectedAANames,
    getSelectedLocationNames,
    getSelectedCallTypeNames,
    getSelectedCallType,
    toArray,
    toNumberArray,
} from '../common/reportingUtils';
import { mapState, mapMutations } from 'vuex';
import Hide from '../common/Hide.vue';
import format from 'date-fns/format';
import { resourceReportFile, resourceAssignmentAreas } from '../../api/resources';
import { getCancelToken } from '../../api';
import { createReportParameters } from './helpers/reportConfiguration';
import {
    secondsToMMSS,
    ifIsNotCanceled,
    getFullDateFormatNoSeconds,
    getFullDateFormat,
} from '../common/helpers';
import MariTimeClock from '../common/MariTimeClock.vue';
import { SUCCESS, ERROR, REPORT_TYPES, EVENT_PAIR_TYPES, ROUTE_NAMES } from '../../constants.js';
import { USER_TYPES } from '../../constants';

export default {
    name: 'ShowReportForm',

    components: {
        ReportSideBar,
        LocationReport,
        FullEvent,
        AscomEvent,
        PairEvent,
        ExceptionReport,
        StaffPerformance,
        SaveReportModal,
        Hide,
        MariTimeClock,
    },

    mixins: [mappingState, reportingUtils],

    constants: {
        REPORT_TYPES,
    },

    props: {
        custom: {
            type: String,
            required: false,
            default: '',
        },

        reportData: {
            type: Object,
            required: true,
        },

        defaultReports: {
            type: Array,
            required: true,
        },

        customReports: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            showSaveReportModal: false,
            timeTillNames: [
                this.$t('settings.targetTimeTab.tillTypes.callAccepted'),
                this.$t('settings.targetTimeTab.tillTypes.nursePresence'),
                this.$t('settings.targetTimeTab.tillTypes.callEnd'),
            ],
            filtersVisibility: true,
            cancelToken: getCancelToken(),
            ascomEventEndTime: '',
        };
    },

    computed: {
        ...mapState({
            aAreas: 'assignmentArea',
            callTypes: 'callTypes',
            unsavedChanges: 'unsavedChanges',
            navCollapsed: 'navCollapsed',
            mariCurrentTime: 'mariCurrentTime',
        }),

        /**
         * Returns component for displaying report
         */
        reportingComponent() {
            let reportTypes = {
                [REPORT_TYPES.LOCATION]: 'LocationReport',
                [REPORT_TYPES.FULL_EVENT]: 'FullEvent',
                [REPORT_TYPES.ASCOM_EVENT]: 'AscomEvent',
                [REPORT_TYPES.PAIR_EVENT]: 'PairEvent',
                [REPORT_TYPES.EXCEPTION]: 'ExceptionReport',
                [REPORT_TYPES.STAFF_PERFORMANCE]: 'StaffPerformance',
            };

            return reportTypes[this.reportData.type];
        },

        defReportName() {
            return this.reportData.name + ' ' + format(new Date(this.mariCurrentTime), getFullDateFormat());
        },

        /**
         * Returns assignment area name for event in Ascom event report
         * @return {String} name of assignment area
         */
        aaForEvent() {
            return (
                this.aAreas.find(area =>
                    area.locations.some(loc => loc.name === this.reportData.event_location)
                ) || []
            ).name;
        },

        /**
         * Forms report parameters object to pass it via props
         * @returns {{
         * aAreas: (Array.String),
         * locations: (Array.String),
         * calltypes: (int[]),
         * timeFrom: (String),
         * timeTo: (String)
         * }}
         */
        reportParameters() {
            let reportDataTmp = Object.assign({}, this.reportData);
            return createReportParameters(
                reportDataTmp,
                this.callTypes,
                this.selectedAANames,
                this.selectedLocationNames,
                this.aaForEvent
            );
        },

        /**
         * Returns selected assignment areas or all assignment areas
         * @return {Object[]} array of assignment area objects
         */
        selectedAANames() {
            return getSelectedAANames(this.reportData, this.aAreas);
        },

        /**
         * Returns names of selected locations
         * @return {String[]} array of locations names
         */
        selectedLocationNames() {
            return getSelectedLocationNames(this.reportData, this.aAreas);
        },

        /**
         * Returns names of selected call types
         * @return {String[]} array of call type names
         */
        selectedCallTypeNames() {
            return getSelectedCallTypeNames(this.reportData, this.callTypes);
        },

        /**
         * Returns value for selected pair of events in pair event report
         * @return {string}
         */
        selectedEventPair() {
            return EVENT_PAIR_TYPES.find(e => e.value === Number(this.reportData.pairEvent)).name;
        },

        /**
         * Forms information about chosen call type's settings for filter table
         * @return {string} 'CallTypeName (target time: mm:ss; delayed time: mm:ss; till: mm:ss)'
         */
        callTypeSettings() {
            let selectedCallType = getSelectedCallType(this.reportData, this.callTypes);
            let settingsPhrase;
            if (selectedCallType.time_target_enabled) {
                let t =
                    this.$t('settings.targetTimeTab.targetTime') +
                    ': ' +
                    secondsToMMSS(selectedCallType.target);
                let d =
                    this.$t('settings.targetTimeTab.delayedTime') +
                    ': ' +
                    secondsToMMSS(selectedCallType.delayed);
                let till =
                    this.$t('settings.targetTimeTab.targetTimeTill') +
                    ': ' +
                    this.timeTillNames[selectedCallType.till - 1];
                settingsPhrase = t + '; ' + d + '; ' + till;
            } else {
                settingsPhrase =
                    this.$t('settings.targetTimeTab.targetTime') +
                    ': ' +
                    this.$t('settings.targetTimeTab.notEnabled');
            }
            return selectedCallType.name + ' (' + settingsPhrase + ')';
        },

        // Returns translation for filter
        startEndTime() {
            let isStartValid =
                this.reportData.startDate === null || this.reportData.startDate === '' ? 'full' : true;
            let isEndValid =
                this.reportData.endDate === null || this.reportData.endDate === '' ? 'full' : true;
            let from = this.reportData.startDate
                ? format(new Date(parseInt(this.reportData.startDate)), getFullDateFormatNoSeconds())
                : '-';
            let to = this.reportData.endDate
                ? format(new Date(parseInt(this.reportData.endDate)), getFullDateFormatNoSeconds())
                : '-';

            if (isStartValid === 'full' && isEndValid === 'full') {
                return this.$t('reports.fullPeriod');
            }

            if (isStartValid && isEndValid) {
                return this.$t('reports.selectedTime', { from: from, to: to });
            }

            if (!isStartValid && isEndValid) {
                return this.$t('reports.endTime', { to: to });
            }

            if (isStartValid && !isEndValid) {
                return this.$t('reports.startTime', { from: from });
            }

            return '';
        },
    },

    watch: {
        $route() {
            this.convertQueryToProps();
            this.setSaveAndExportButtons();
            this.setSelectedReportType(this.reportData.type);
        },
        navCollapsed() {
            window.dispatchEvent(new Event('resize'));
        },
    },

    created() {
        this.convertQueryToProps();
        this.setSaveAndExportButtons();
        this.setSelectedReportType(this.reportData.type);
        let id = this.custom === 'custom' ? REPORT_TYPES.ASCOM_EVENT : this.reportData.id;
        let update = this.$store.state.updateData;
        this.updateData(() => {
            this.$router.push({
                name: ROUTE_NAMES.REPORTS,
                params: { id: id },
            });
            this.showLoading(false);
            return update();
        });
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ setSelectedReportType: 'SET_SELECTED_REPORT_TYPE', updateData: 'SET_UPDATE_DATA' }),

        /**
         * Returns full period if start date and end date are empty and valid status if this values are not empty
         * @param {string} date
         * @param {string} time
         * @return {string|boolean}
         */
        isTimeValid(date) {
            if (!this.reportData.startDate && !this.reportData.endDate) {
                return 'full';
            }
            if (typeof date !== 'object') {
                return false;
            }

            return true;
        },

        exportAsCSV() {
            return this.getFile('csv');
        },

        exportAsPDF() {
            return this.getFile('pdf');
        },

        /**
         * Executes request to the backend for getting a report file by file type and current report type id
         * Saves the report file to user hard disc
         * @param type
         */
        getFile(type) {
            this.showLoading(true);
            resourceReportFile({
                reportId: this.reportData.type,
                docType: type,
                data:
                    this.custom === 'custom'
                        ? { ...this.reportParameters, report_name: this.reportData.name }
                        : this.reportParameters,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    let fileBody = response.data;
                    let fileName = this.getFileName(response);

                    // For downloading a file in IE needs to execute special method "msSaveOrOpenBlob"
                    if (navigator.msSaveOrOpenBlob) {
                        navigator.msSaveOrOpenBlob(fileBody, fileName);
                    } else {
                        let link = this.$refs.download;
                        link.href = window.URL.createObjectURL(fileBody);
                        link.download = fileName;
                        link.click();
                    }
                    this.showLoading(false);
                })
                .catch(error => {
                    ifIsNotCanceled({
                        callback: response => {
                            if (response instanceof DOMException) {
                                this.popup({
                                    type: ERROR,
                                    header: this.$t('common.error'),
                                    text: this.$t('reports.exportError', { type }),
                                });
                            }
                        },
                    })(error);
                });
        },

        getFileName(response) {
            let fileName = '';
            let disposition = response.headers['content-disposition'];
            if (disposition && ~disposition.indexOf('attachment')) {
                let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                let matches = filenameRegex.exec(disposition);
                if (matches && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            return decodeURIComponent(fileName.replace(/\+/g, ' '));
        },

        /**
         * Converts data of query for assignment areas, location and call types to array             *
         */
        convertQueryToProps() {
            this.setUnsavedChanges(
                typeof this.reportData.isChanged === 'string'
                    ? this.reportData.isChanged === 'true'
                    : this.reportData.isChanged
            );
            this.reportData.type = Number(this.reportData.type);
            this.reportData.aa = toArray(this.reportData.aa);
            this.reportData.loc = toArray(this.reportData.loc);
            this.reportData.ct = toNumberArray(this.reportData.ct);
            this.reportData.incomplete = String(this.reportData.incomplete) === 'true';
        },

        /**
         * Returns to report configuration page
         */
        back() {
            let routeName =
                this.custom === 'custom' ? ROUTE_NAMES.REPORTS__CUSTOM__EDIT : ROUTE_NAMES.REPORTS__EDIT;
            let currentIsChanged = this.unsavedChanges;
            this.setUnsavedChanges(false);
            this.$router.push({
                name: routeName,
                params: { id: this.reportData.id },
                // current unsavedChanges status higher priority then status from url query
                query: { ...this.reportData, isChanged: currentIsChanged },
            });
        },

        setSaveAndExportButtons() {
            this.configButton([
                {
                    buttonName: 'exportButtonCsv',
                    action: () => {
                        this.exportAsCSV();
                    },
                },
                {
                    buttonName: 'exportButtonPdf',
                    action: () => {
                        this.exportAsPDF();
                    },
                },
                {
                    buttonName: 'saveButton',
                    action: () => {
                        this.defineSaveMode();
                    },
                    disabled: () => this.custom === 'custom' && !this.unsavedChanges,
                },
            ]);
        },

        defineSaveMode() {
            if (this.custom === 'custom') {
                let report = {
                    reportName: this.reportData.name,
                    emailingTime:
                        this.reportData.schedule_type !== null && this.reportData.emailing_time
                            ? this.reportData.emailing_time
                            : '00:00',
                    scheduleEmails:
                        this.reportData.schedule_type !== null ? this.reportData.schedule_emails : [],
                    scheduleType: this.reportData.schedule_type,
                    scheduleDescription:
                        this.reportData.schedule_type !== null ? this.reportData.schedule_description : null,
                };
                this.save(this.reportData.id, report);
            } else {
                this.showSaveReportModal = true;
            }
        },

        saveDefaultReport(report) {
            this.save(null, report);
        },

        saveReport(reportId, report) {
            return saveReport(
                reportId,
                getCustomReportObject({ ...this.reportData, ...report }, this.aAreas, this.callTypes)
            )
                .then(response => {
                    this.setUnsavedChanges(false);
                    this.popup({
                        type: SUCCESS,
                        header: this.$t('common.success'),
                        text: this.$t('common.saved'),
                    });
                    this.$emit('refresh');
                    this.updateQuery(response.data, report);
                    this.showLoading(false);
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            this.handleError(response.status);
                        },
                    })
                );
        },

        save(reportId, report) {
            this.showLoading(true);
            if (this.userType === USER_TYPES.SUPER_USER) {
                resourceAssignmentAreas()
                    .then(response => {
                        if (response.data) {
                            if (response.data[0].id !== this.userAreaId) {
                                this.showLoading(false);
                                this.popup({
                                    type: ERROR,
                                    header: this.$t('common.error'),
                                    text: this.$t('error.common.forbidden'),
                                });
                                return Promise.reject(response);
                            }
                            return this.saveReport(reportId, report);
                        }
                    })
                    .catch(error => {
                        this.showLoading(false);
                        if (error.status !== 200) {
                            this.popup({
                                type: ERROR,
                                header: this.$t('common.error'),
                                text: this.$t('error.common.forbidden'),
                            });
                        }
                    });
            } else {
                return this.saveReport(reportId, report);
            }
        },

        /**
         * Updates url query with new report data
         * @param responseData
         * @param report
         */
        updateQuery(responseData, report) {
            if (this.custom !== 'custom') {
                if (responseData && responseData.report_id) {
                    let updatedQuery = {
                        ...this.reportData,
                        id: responseData.report_id,
                        name: report.reportName.trim(),
                        typeName: this.reportData.name,
                        emailing_time: report.emailingTime,
                        schedule_emails: report.scheduleEmails,
                        schedule_type: report.scheduleType,
                        schedule_description: report.scheduleDescription,
                        isChanged: false,
                    };

                    this.$nextTick(() =>
                        this.$router.replace({
                            name: ROUTE_NAMES.REPORTS__SHOW,
                            query: updatedQuery,
                            params: { custom: 'custom' },
                        })
                    );
                }
            } else {
                this.$nextTick(() => {
                    this.$router.replace({
                        name: this.$route.name,
                        query: {
                            ...this.$route.query,
                            isChanged: false,
                        },
                        params: {
                            ...this.$route.params,
                            id: this.reportData.id,
                        },
                    });
                });
            }
        },

        /**
         * Handles api request error depending on status
         * @param status
         */
        handleError(status) {
            if (Number(status) === 404) {
                // Wait 3 sec while user reads tooltip before redirect
                setTimeout(() => {
                    this.$emit('refresh');
                    this.showLoading(false);
                    this.setUnsavedChanges(false);
                    this.$nextTick(() => {
                        this.$router.replace({ name: ROUTE_NAMES.REPORTS });
                    });
                }, 3000);
            } else {
                if (status === 409) {
                    this.$emit('refresh');
                }
                this.showLoading(false);
            }
        },

        /**
         * Calculates Ascom event end time in filters
         * @param dateTime
         */
        setAscomEventEndTime(dateTime) {
            if (dateTime) {
                this.ascomEventEndTime = this.formatDateString(dateTime);
            } else {
                this.ascomEventEndTime = this.reportData.incomplete
                    ? this.$t('reports.unknown')
                    : this.$t('reports.ongoing');
            }
        },
    },
};
</script>
